/**
 * Sidebar Navigation JavaScript
 * Handles sidebar functionality including toggle, category expansion, and mobile support
 */

class SidebarNavigation {
  constructor() {
    this.sidebar = document.querySelector('[data-sidebar-navigation]');
    this.mobileToggle = document.querySelector('.sidebar-navigation__mobile-toggle');
    this.closeBtn = document.querySelector('.sidebar-navigation__close-btn');
    this.categoryHeaders = document.querySelectorAll('.sidebar-navigation__category-header');
    this.body = document.body;
    this.isOpen = false;
    this.overlay = null;
    
    this.init();
  }

  init() {
    if (!this.sidebar) return;
    
    // Check if this is an integrated sidebar
    this.isIntegrated = this.sidebar.classList.contains('sidebar-navigation--integrated');
    
    if (!this.isIntegrated) {
      this.createOverlay();
      this.bindEvents();
      this.setActiveLink();
      this.handleResize();
      
      // Show sidebar by default on desktop
      if (window.innerWidth > 1024) {
        this.openSidebar();
      }
    } else {
      // For integrated sidebars, just set active links and bind category events
      this.bindCategoryEvents();
      this.setActiveLink();
    }
  }

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'sidebar-overlay';
    this.overlay.addEventListener('click', () => this.closeSidebar());
    document.body.appendChild(this.overlay);
  }

  bindEvents() {
    // Mobile toggle button
    if (this.mobileToggle) {
      this.mobileToggle.addEventListener('click', () => this.toggleSidebar());
    }

    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeSidebar());
    }

    // Category headers for expand/collapse
    this.categoryHeaders.forEach(header => {
      header.addEventListener('click', (e) => this.toggleCategory(e));
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => this.handleKeyboard(e));

    // Window resize
    window.addEventListener('resize', () => this.handleResize());

    // Click outside to close (mobile)
    document.addEventListener('click', (e) => this.handleOutsideClick(e));
  }

  bindCategoryEvents() {
    // Only bind category expansion events for integrated sidebars
    this.categoryHeaders.forEach(header => {
      header.addEventListener('click', (e) => this.toggleCategory(e));
    });
  }

  toggleSidebar() {
    if (this.isIntegrated) return; // Don't toggle integrated sidebars
    
    if (this.isOpen) {
      this.closeSidebar();
    } else {
      this.openSidebar();
    }
  }

  openSidebar() {
    if (this.isIntegrated) return; // Don't open integrated sidebars
    
    this.sidebar.classList.add('is-open');
    this.overlay.classList.add('is-visible');
    this.body.classList.add('has-sidebar');
    this.isOpen = true;
    
    // Focus management
    this.sidebar.setAttribute('aria-hidden', 'false');
    this.sidebar.focus();
    
    // Announce to screen readers
    this.announceToScreenReader('Sidebar opened');
  }

  closeSidebar() {
    if (this.isIntegrated) return; // Don't close integrated sidebars
    
    this.sidebar.classList.remove('is-open');
    this.overlay.classList.remove('is-visible');
    this.body.classList.remove('has-sidebar');
    this.isOpen = false;
    
    // Focus management
    this.sidebar.setAttribute('aria-hidden', 'true');
    
    // Return focus to mobile toggle if it exists
    if (this.mobileToggle && window.innerWidth <= 1024) {
      this.mobileToggle.focus();
    }
    
    // Announce to screen readers
    this.announceToScreenReader('Sidebar closed');
  }

  toggleCategory(event) {
    const category = event.currentTarget.closest('.sidebar-navigation__category');
    const isOpen = category.classList.contains('is-open');
    
    // Close all other categories
    this.categoryHeaders.forEach(header => {
      const otherCategory = header.closest('.sidebar-navigation__category');
      if (otherCategory !== category) {
        otherCategory.classList.remove('is-open');
      }
    });
    
    // Toggle current category
    category.classList.toggle('is-open');
    
    // Announce to screen readers
    const categoryTitle = event.currentTarget.querySelector('.sidebar-navigation__category-title').textContent;
    const action = category.classList.contains('is-open') ? 'expanded' : 'collapsed';
    this.announceToScreenReader(`${categoryTitle} category ${action}`);
  }

  setActiveLink() {
    const currentPath = window.location.pathname;
    const links = this.sidebar.querySelectorAll('.sidebar-navigation__link');
    
    links.forEach(link => {
      if (link.getAttribute('href') === currentPath) {
        link.classList.add('active');
        
        // Expand parent category
        const category = link.closest('.sidebar-navigation__category');
        if (category) {
          category.classList.add('is-open');
        }
      }
    });
  }

  handleKeyboard(event) {
    if (this.isIntegrated || !this.isOpen) return;
    
    switch (event.key) {
      case 'Escape':
        this.closeSidebar();
        break;
      case 'Tab':
        this.handleTabNavigation(event);
        break;
    }
  }

  handleTabNavigation(event) {
    if (this.isIntegrated) return; // Don't handle tab navigation for integrated sidebars
    
    const focusableElements = this.sidebar.querySelectorAll(
      'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  handleOutsideClick(event) {
    if (this.isIntegrated || !this.isOpen || window.innerWidth > 1024) return;
    
    if (!this.sidebar.contains(event.target) && !this.mobileToggle.contains(event.target)) {
      this.closeSidebar();
    }
  }

  handleResize() {
    if (this.isIntegrated) return; // Don't handle resize for integrated sidebars
    
    if (window.innerWidth > 1024) {
      // Desktop: show sidebar by default
      if (!this.isOpen) {
        this.openSidebar();
      }
      this.overlay.classList.remove('is-visible');
    } else {
      // Mobile: hide sidebar by default
      if (this.isOpen) {
        this.closeSidebar();
      }
    }
  }

  announceToScreenReader(message) {
    // Create a temporary element for screen reader announcements
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  // Public method to programmatically open/close sidebar
  setState(open) {
    if (this.isIntegrated) return; // Don't change state for integrated sidebars
    
    if (open) {
      this.openSidebar();
    } else {
      this.closeSidebar();
    }
  }

  // Public method to check if sidebar is open
  getState() {
    if (this.isIntegrated) return true; // Integrated sidebars are always "open"
    return this.isOpen;
  }
}

// Initialize sidebar navigation when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new SidebarNavigation();
});

// Export for potential external use
window.SidebarNavigation = SidebarNavigation;

// Handle Shopify section reloads
document.addEventListener('shopify:section:load', () => {
  new SidebarNavigation();
});

// Add CSS for screen reader only content
const srOnlyStyles = document.createElement('style');
srOnlyStyles.textContent = `
  .sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }
`;
document.head.appendChild(srOnlyStyles);
